'use client'
import * as React from 'react'
import Image from 'next/image'
import { ArrowUpRight, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { idukkiPicks, CATEGORIES, MARKETS, STACKS, type Pick } from '@/lib/copy/idukki-picks'
import { unsplashPlaceholderFor } from '@/lib/copy/placeholder-images'

type FilterKey = 'category' | 'market' | 'stack'

export function PicksDirectory() {
  const [query, setQuery] = React.useState('')
  const [filters, setFilters] = React.useState<{ category?: string; market?: string; stack?: string }>({})

  const filtered = idukkiPicks.filter((p) => {
    if (filters.category && p.category !== filters.category) return false
    if (filters.market && p.market !== filters.market) return false
    if (filters.stack && !p.stack.includes(filters.stack as Pick['stack'][number])) return false
    if (query) {
      const q = query.toLowerCase()
      if (!`${p.brand} ${p.body} ${p.whyIt}`.toLowerCase().includes(q)) return false
    }
    return true
  })

  function toggle(key: FilterKey, value: string) {
    setFilters((f) => ({ ...f, [key]: f[key] === value ? undefined : value }))
  }

  return (
    <div>
      <div className="rounded-2xl border bg-card p-5 sm:p-6">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search brands or patterns…"
            className="pl-10"
          />
        </div>
        <FilterRow label="Category" options={CATEGORIES} active={filters.category} onToggle={(v) => toggle('category', v)} />
        <FilterRow label="Market" options={MARKETS} active={filters.market} onToggle={(v) => toggle('market', v)} />
        <FilterRow label="Stack" options={STACKS} active={filters.stack} onToggle={(v) => toggle('stack', v)} />
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <span>{filtered.length} of {idukkiPicks.length} brands</span>
          {(query || filters.category || filters.market || filters.stack) && (
            <button
              type="button"
              className="rounded-md border px-2 py-1 transition hover:border-primary/40 hover:text-foreground"
              onClick={() => { setQuery(''); setFilters({}) }}
            >
              Reset filters
            </button>
          )}
        </div>
      </div>

      <ul className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((p) => (
          <li key={p.slug}>
            <a
              href={p.url}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex h-full flex-col overflow-hidden rounded-md border bg-card transition hover:border-primary/40 hover:shadow-md"
            >
              <div className="relative aspect-[16/10] overflow-hidden">
                <Image
                  src={unsplashPlaceholderFor(p.slug, { category: p.imageCategory, width: 1200, height: 750 })}
                  alt={p.brand}
                  fill
                  sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
                  className="object-cover transition duration-500 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute inset-x-0 top-0 flex items-center justify-between p-4">
                  <span className="rounded-full bg-white/95 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-foreground shadow-sm">{p.category}</span>
                  <span className="rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white">{p.market}</span>
                </div>
                <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-4 text-white">
                  <div className="font-display text-xl font-medium drop-shadow">{p.brand}</div>
                  <ArrowUpRight className="h-5 w-5 opacity-90 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </div>
              </div>
              <div className="flex flex-1 flex-col p-5">
                <p className="text-sm text-muted-foreground">{p.body}</p>
                <p className="mt-3 rounded-md bg-primary/5 px-3 py-2 text-xs text-primary">
                  <strong className="font-medium">Why we picked it: </strong>{p.whyIt}
                </p>
                <div className="mt-4 flex flex-wrap gap-1.5">
                  {p.stack.map((s) => (
                    <span key={s} className="rounded-full border bg-secondary px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">{s}</span>
                  ))}
                </div>
              </div>
            </a>
          </li>
        ))}
      </ul>

      {filtered.length === 0 && (
        <div className="mt-10 rounded-md border border-dashed bg-card p-10 text-center text-sm text-muted-foreground">
          No brands match your filters. Reset and try again.
        </div>
      )}
    </div>
  )
}

function FilterRow({ label, options, active, onToggle }: { label: string; options: readonly string[]; active?: string; onToggle: (v: string) => void }) {
  return (
    <div className="mt-4">
      <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {options.map((opt) => (
          <button
            type="button"
            key={opt}
            onClick={() => onToggle(opt)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs transition',
              active === opt ? 'border-primary bg-primary/10 text-primary' : 'border-input bg-card hover:border-primary/40 hover:text-foreground',
            )}
          >
            {opt}
          </button>
        ))}
      </div>
    </div>
  )
}
